/**
 * Keeps the add-to-cart button on collection and homepage product cards in sync with the
 * selected variant, switching it to a "Notify me" link when that variant is unavailable.
 *
 * Loaded once per page. It listens on document level and watches for cards that Horizon
 * injects later (sliders, filtering, variant changes), so it never needs to be re-run.
 */
(() => {
  if (window.collectionNotifyButtonInitialized) return;
  window.collectionNotifyButtonInitialized = true;

  const buttonSelector = '[data-collection-add-to-cart]';

  function getVariants(button) {
    try {
      return JSON.parse(button.dataset.productVariants || '[]');
    } catch (error) {
      console.error('Unable to parse product variants:', error);
      return [];
    }
  }

  function getVariantInput(button) {
    const form = button.closest('form');

    if (form) {
      const formVariantInput =
        form.querySelector('input[name="id"]') ||
        form.querySelector('select[name="id"]') ||
        form.querySelector('[name="id"]');

      if (formVariantInput) {
        return formVariantInput;
      }
    }

    const productCard =
      button.closest('product-card') ||
      button.closest('[data-product-id]') ||
      button.closest('.product-card') ||
      button.closest('.product-card-wrapper');

    if (!productCard) return null;

    return (
      productCard.querySelector('input[name="id"]') ||
      productCard.querySelector('select[name="id"]') ||
      productCard.querySelector('[name="id"]')
    );
  }

  function setNotifyState(button) {
    const label = button.querySelector('[data-add-to-cart-label]');

    button.dataset.notifyMode = 'true';
    button.type = 'button';
    button.disabled = false;

    button.removeAttribute('disabled');
    button.setAttribute('aria-disabled', 'false');

    if (label) {
      label.textContent = button.dataset.notifyText || 'Notify me';
    }
  }

  function setAddToCartState(button) {
    const label = button.querySelector('[data-add-to-cart-label]');

    button.dataset.notifyMode = 'false';
    button.type = 'submit';
    button.disabled = false;

    button.removeAttribute('disabled');
    button.removeAttribute('aria-disabled');

    if (label) {
      label.textContent = button.dataset.addText || 'Add to cart';
    }
  }

  // Shade blocked by custom.allow_backorder = false: disabled "Sold out", same as the PDP.
  function setSoldOutState(button) {
    const label = button.querySelector('[data-add-to-cart-label]');

    button.dataset.notifyMode = 'false';
    button.type = 'button';
    button.disabled = true;

    button.setAttribute('disabled', '');
    button.setAttribute('aria-disabled', 'true');

    if (label) {
      label.textContent = button.dataset.soldOutText || 'Sold out';
    }
  }

  function updateButtonFromSelectedVariant(button) {
    if (!button) return;

    const variants = getVariants(button);
    const variantInput = getVariantInput(button);

    if (!variantInput || variants.length === 0) {
      return;
    }

    const selectedVariantId = String(variantInput.value);

    if (!selectedVariantId) return;

    const selectedVariant = variants.find((variant) => {
      return String(variant.id) === selectedVariantId;
    });

    if (!selectedVariant) {
      console.warn('Selected variant not found:', selectedVariantId);
      return;
    }

    if (selectedVariant.blocked) {
      setSoldOutState(button);
    } else if (selectedVariant.available) {
      setAddToCartState(button);
    } else {
      setNotifyState(button);
    }
  }

  function initializeButton(button) {
    if (!button || button.dataset.notifyInitialized === 'true') {
      return;
    }

    button.dataset.notifyInitialized = 'true';

    updateButtonFromSelectedVariant(button);

    // Horizon updates the variant input's value attribute rather than dispatching on the button.
    const variantInput = getVariantInput(button);

    if (variantInput) {
      const variantObserver = new MutationObserver(() => {
        updateButtonFromSelectedVariant(button);
      });

      variantObserver.observe(variantInput, {
        attributes: true,
        attributeFilter: ['value'],
      });
    }
  }

  function initializeButtons(container = document) {
    if (container.matches?.(buttonSelector)) {
      initializeButton(container);
    }

    container.querySelectorAll?.(buttonSelector).forEach((button) => {
      initializeButton(button);
    });
  }

  document.addEventListener('change', (event) => {
    const productCard =
      event.target.closest('product-card') ||
      event.target.closest('[data-product-id]') ||
      event.target.closest('.product-card') ||
      event.target.closest('.product-card-wrapper') ||
      event.target.closest('form');

    if (!productCard) return;

    // Horizon sometimes updates the hidden variant ID after the change event, so wait for it.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const button = productCard.querySelector(buttonSelector);

        if (button) {
          updateButtonFromSelectedVariant(button);
        }
      });
    });
  });

  // Some Horizon swatches use clicks and update the variant asynchronously.
  document.addEventListener('click', (event) => {
    const clickedOption = event.target.closest(
      'input, label, button, [data-option-value], [data-variant-id]'
    );

    if (!clickedOption) return;

    const productCard =
      clickedOption.closest('product-card') ||
      clickedOption.closest('[data-product-id]') ||
      clickedOption.closest('.product-card') ||
      clickedOption.closest('.product-card-wrapper') ||
      clickedOption.closest('form');

    if (!productCard) return;

    const button = productCard.querySelector(buttonSelector);

    if (!button) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateButtonFromSelectedVariant(button);
      });
    });
  });

  // Notify me redirects to /products/product-handle?variant=VARIANT_ID
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest(buttonSelector);

      if (!button || button.dataset.notifyMode !== 'true') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (!button.dataset.productUrl) return;

      const variantInput = getVariantInput(button);
      const selectedVariantId = variantInput?.value;

      const url = new URL(button.dataset.productUrl, window.location.origin);

      if (selectedVariantId) {
        url.searchParams.set('variant', selectedVariantId);
      }

      window.location.href = url.toString();
    },
    true
  );

  function start() {
    initializeButtons(document);

    // Horizon replaces product-card HTML on sliders, filtering and variant changes.
    const pageObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) {
            return;
          }

          initializeButtons(node);

          const productCard =
            node.closest?.('product-card') ||
            node.closest?.('[data-product-id]') ||
            node.closest?.('.product-card') ||
            node.closest?.('.product-card-wrapper');

          if (!productCard) return;

          const button = productCard.querySelector(buttonSelector);

          if (button) {
            updateButtonFromSelectedVariant(button);
          }
        });
      });
    });

    pageObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();